<script setup lang="ts">
import type { CookieItem } from '~/types/content/cookieItem.type';

const cookies: Record<string, CookieItem[]> = {
    necessary: [
        {
            name: '_GRECAPTCHA',
            provider: 'google.com',
            purpose:
                'Esta cookie se utiliza para distinguir entre humanos y robots. Esto es útil para el sitio web, con el fin de generar informes válidos sobre el uso del sitio.',
            expiry: '180 días',
            type: 'HTTP Cookie',
        },
        {
            name: 'CookieConsent',
            provider: 'consent.cookiebot.com',
            purpose: 'Almacena el estado del consentimiento de cookies del usuario para el dominio actual',
            expiry: '1 año',
            type: 'HTTP Cookie',
        },
        {
            name: 'iconify-count',
            provider: 'fleequid.com',
            purpose:
                'Utilizado por el Sistema de Gestión de Contenidos (CMS) del sitio web para determinar cómo deben mostrarse los menús de pestañas del sitio.',
            expiry: 'Persistente',
            type: 'HTML Local Storage',
        },
        {
            name: 'iconify-version',
            provider: 'fleequid.com',
            purpose:
                'Utilizado por el Sistema de Gestión de Contenidos (CMS) del sitio web para determinar cómo deben mostrarse los menús de pestañas del sitio.',
            expiry: 'Persistente',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::a',
            provider: 'gstatic.com',
            purpose:
                'Esta cookie se utiliza para distinguir entre humanos y robots. Esto es útil para el sitio web, con el fin de generar informes válidos sobre el uso del sitio.',
            expiry: 'Persistente',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::b',
            provider: 'gstatic.com',
            purpose: 'Esta cookie se utiliza para distinguir entre humanos y robots.',
            expiry: 'Persistente',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::c',
            provider: 'gstatic.com',
            purpose: 'Esta cookie se utiliza para distinguir entre humanos y robots.',
            expiry: 'Persistente',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::f',
            provider: 'gstatic.com',
            purpose: 'Esta cookie se utiliza para distinguir entre humanos y robots.',
            expiry: 'Persistente',
            type: 'HTML Local Storage',
        },
    ],
    preferences: [
        {
            name: 'i18n_redirected',
            provider: 'fleequid.com',
            purpose: 'Determina el idioma preferido del visitante. Permite al sitio web establecer el idioma preferido cuando el visitante regresa.',
            expiry: '1 año',
            type: 'HTTP Cookie',
        },
    ],
    marketing: [
        {
            name: '_ga',
            provider: 'www.googletagmanager.com',
            purpose:
                'Utilizado para enviar datos a Google Analytics sobre el dispositivo y el comportamiento del usuario. Rastrea al usuario a través de dispositivos y canales de marketing.',
            expiry: '2 años',
            type: 'HTTP Cookie',
        },
        {
            name: '_ga#',
            provider: 'www.googletagmanager.com',
            purpose:
                'Utilizado para enviar datos a Google Analytics sobre el dispositivo y el comportamiento del usuario. Rastrea al usuario a través de dispositivos y canales de marketing.',
            expiry: '2 años',
            type: 'HTTP Cookie',
        },
    ],
    unclassified: [
        {
            name: 'nuxt-color-mode',
            provider: 'fleequid.com',
            purpose: 'Determina el tema preferido del visitante.',
            expiry: 'Persistente',
            type: 'HTML Local Storage',
        },
    ],
};
</script>
<template>
    <ContentPage title="Política de Cookies">
        <p>
            Utilizamos cookies para personalizar contenidos y anuncios, para proporcionar funciones de redes sociales y para analizar nuestro tráfico.
            También compartimos información sobre cómo utiliza nuestro sitio con nuestros socios de análisis web, publicidad y redes sociales, quienes
            podrían combinarla con otra información.
        </p>
        <p>Las cookies son pequeños archivos de texto que los sitios web pueden utilizar para hacer más eficiente la experiencia del usuario.</p>
        <p>
            La ley establece que podemos almacenar cookies en su dispositivo si son estrictamente necesarias para el funcionamiento de este sitio.
            Para todos los demás tipos de cookies necesitamos su consentimiento.
        </p>
        <p>
            Este sitio utiliza diferentes tipos de cookies. Algunas cookies son colocadas por servicios de terceros que aparecen en nuestras páginas.
        </p>
        <p>En cualquier momento puede cambiar o retirar su consentimiento desde la Declaración de cookies en nuestro sitio web.</p>
        <p>
            Descubra más sobre quiénes somos, cómo puede contactarnos y cómo tratamos los datos personales en nuestra
            <NuxtLinkLocale to="privacy-policy" class="text-primary-500 hover:underline">Política de privacidad</NuxtLinkLocale>.
        </p>
        <p>Especifique su ID de consentimiento y la fecha en que nos contactó respecto a su consentimiento.</p>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Necesarias (8)</h3>
            <p>
                Las cookies necesarias ayudan a hacer que un sitio web sea utilizable habilitando funciones básicas como la navegación por páginas y
                el acceso a áreas seguras del sitio web. El sitio web no puede funcionar correctamente sin estas cookies.
            </p>
            <ContentCookieTable :items="cookies.necessary" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Preferencias (1)</h3>
            <p>
                Las cookies de preferencias permiten al sitio web recordar información que cambia la forma en que se comporta o se ve el sitio web,
                como su idioma preferido o la región en la que se encuentra.
            </p>
            <ContentCookieTable :items="cookies.preferences" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Marketing (2)</h3>
            <p>
                Las cookies de marketing se utilizan para rastrear a los visitantes en los sitios web. La intención es mostrar anuncios que sean
                relevantes y atractivos para el usuario individual y, por lo tanto, más valiosos para los editores y anunciantes externos.
            </p>
            <ContentCookieTable :items="cookies.marketing" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">No clasificadas (1)</h3>
            <p>Las cookies no clasificadas son cookies que están en proceso de clasificación, junto con los proveedores de cookies individuales.</p>
            <ContentCookieTable :items="cookies.unclassified" />
        </div>
    </ContentPage>
</template>
